import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { BORDER_RADIUS_LARGE } from '../../constants'
import { CreationToolSourceFile } from '../../types'
import CreationToolOutputType from '../../types/output-type'
import FrameStrip from '../frame-strip'
import RangeSlider from '../range-slider'
import Inputs from './inputs'

const Container = styled.div`
    border-radius: ${BORDER_RADIUS_LARGE}px;
`

const Strip = styled(FrameStrip)`
    height: 66px;
`

const Slider = styled(RangeSlider)`
    background: #000;
    border-radius: inherit;
`

const Playhead = styled.div`
    bottom: 0;
    box-sizing: border-box;
    left: 16px;
    padding: 3px 0;
    pointer-events: none;
    position: absolute;
    right: 16px;
    top: 0;
    z-index: 1;

    &:before {
        background: #fff;
        border-radius: 2px;
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.5);
        content: '';
        display: block;
        height: 100%;
        margin-left: -2px;
        width: 4px;
    }
`

type Props = {
    source: CreationToolSourceFile
    outputType: CreationToolOutputType
    playheadTime?: number
    isInvalid?: boolean
    showInputs?: boolean
    onUpdate: (startTime: number, endTime: number, isTrimmed: boolean) => void
}

const CreationToolsTrimmer = ({
    source,
    outputType,
    playheadTime,
    showInputs = false,
    isInvalid = false,
    onUpdate,
}: Props) => {
    const maxDuration = CreationToolOutputType.maxDuration(outputType)

    const [startTime, setStartTime] = useState<number>(0)
    const [endTime, setEndTime] = useState<number>(Math.min(maxDuration, source.duration))

    useEffect(() => {
        const duration = endTime - startTime
        onUpdate(startTime, endTime, duration < source.duration)
    }, [startTime, endTime, source])

    const onTimeUpdate = (start: number, end: number) => {
        setStartTime(start)
        setEndTime(end)
    }

    const onRangeUpdate = (start: number, end: number) => {
        const startTime = start * source.duration
        const endTime = end * source.duration
        setStartTime(startTime)
        setEndTime(endTime)
    }

    return (
        <Container>
            <Slider
                {...{
                    start: startTime / source.duration,
                    end: endTime / source.duration,
                    isInvalid,
                    onUpdate: onRangeUpdate,
                }}
            >
                <Strip source={source} />
                {typeof playheadTime === 'number' && (
                    <Playhead style={{ transform: `translate3d(${(playheadTime / source.duration) * 100}%, 0, 0)` }} />
                )}
            </Slider>
            {showInputs && (
                <Inputs
                    {...{
                        duration: source.duration,
                        startTime,
                        endTime,
                        isInvalid,
                        onUpdate: onTimeUpdate,
                    }}
                />
            )}
        </Container>
    )
}

export default CreationToolsTrimmer
